import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { TimeAgo } from "@/components/TimeAgo"
import ChannelBars from "@/components/home/ChannelBars"
import HeadlineTiles from "@/components/home/HeadlineTiles"
import ListCard from "@/components/home/ListCard"
import MonthlyRevenueChart from "@/components/home/MonthlyRevenueChart"
import PlanBadge from "@/components/home/PlanBadge"
import {
  type ExcessProduct, type MarketOverview, type MarketSnapshot, type TopBuy, monthLabel,
} from "@/components/home/types"
import { Badge } from "@/components/ui/Badge"
import { formatMoneyShort } from "@/lib/money"
import { inventory_overview_path, plan_index_path, purchase_products_path, push_review_path, revenue_path } from "@/lib/routes"
import { useMarket } from "@/lib/useMarket"
import { formatRange } from "../Revenue/format"

interface Props {
  snapshot: MarketSnapshot
  overview: MarketOverview
  top_buys: TopBuy[]
}

const units = (n: number) => `${n.toLocaleString("en-US")} ${n === 1 ? "unit" : "units"}`

function excessNote(p: ExcessProduct, currency: string): string {
  return `${formatMoneyShort(p.excess_value, currency)} · ${p.excess_units.toLocaleString("en-US")} excess`
}

export default function Show({ snapshot, overview, top_buys }: Props) {
  const market = useMarket()
  const cur = snapshot.currency
  const { plan, excess } = snapshot
  const planning = overview.planning

  return (
    <AppLayout
      header={
        <div className="flex items-center gap-2">
          <PageTitle title={market.name} />
          <Badge variant="outline">{market.currency}</Badge>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <HeadlineTiles
          currency={cur}
          revenueThisYear={snapshot.revenue_this_year}
          revenueThisMonth={snapshot.revenue_this_month}
          changePct={snapshot.change_pct}
          plan={{
            target: plan.target ?? null,
            revenue: plan.revenue ?? null,
            period: plan.begin_date && plan.end_date ? formatRange(plan.begin_date, plan.end_date) : undefined,
          }}
          needBuying={snapshot.need_buying}
          needBuyingHref={inventory_overview_path({ market_slug: market.slug })}
          excess={excess}
          excessHref={push_review_path(market.slug)}
        />

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <MonthlyRevenueChart
              title="Revenue by month"
              subtitle="Actual revenue against the monthly plan. The plan line shows only for months that have a monthly plan."
              currency={cur}
              data={overview.monthly.map((m) => ({ label: monthLabel(m.key), actual: m.actual, plan: m.plan }))}
              bars={[{ key: "actual", name: "Actual", color: "var(--series-1)" }]}
              line={{ key: "plan", name: "Plan" }}
              footer={{ label: "Open Revenue", href: revenue_path(market.slug) }}
            />
          </div>
          <ChannelBars channels={overview.channels} currency={cur} />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <ListCard
            title="Biggest orders needed"
            rows={top_buys.map((p) => ({
              label: p.name,
              productExternalId: p.product_external_id,
              value: <span className="font-medium text-warning">order {units(p.quantity)}</span>,
            }))}
            empty="Nothing needs buying right now."
            footer={{ label: "Open Purchase Plan", href: purchase_products_path() }}
          />
          <ListCard
            title="Most excess stock"
            rows={overview.most_excess.map((p) => ({
              label: p.name,
              productExternalId: p.product_external_id,
              value: excessNote(p, cur),
            }))}
            empty="No excess stock right now."
            footer={{ label: "Open Push Review", href: push_review_path(market.slug) }}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <ListCard
            title="Planning"
            rows={[
              { label: plan.name ? `Top Down plan: ${plan.name}` : "Top Down plan", value: <PlanBadge plan={plan} withVersion /> },
              ...(planning
                ? [{ label: "Bottom Up by month", value: `${planning.months_filled} of ${planning.months_total} months filled` }]
                : []),
            ]}
            footer={{ label: "Open Planning", href: plan_index_path(market.slug) }}
          />
          <ListCard
            title="Data last updated"
            rows={overview.data_updated.map((d) => ({
              label: d.label,
              value: d.last_synced_at ? <TimeAgo at={d.last_synced_at} /> : "Never",
            }))}
            empty="No data sources set up for this market yet."
          />
        </div>
      </div>
    </AppLayout>
  )
}
